import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { getActiveSubscription } from "../redux/actions/subscription.js";
import { isUserLoggedIn, getLoggedInUser } from "../utils/helpers.js";
import { QUERY_KEYS } from "../utils/config.js";

/**
 * Hook to sync subscription data on app initialization and window focus
 * This ensures subscription changes from mobile app are reflected in web app
 */
export const useSubscriptionSync = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isAuthenticated = isUserLoggedIn();
  const user = getLoggedInUser();

  // Refresh subscription data
  const refreshSubscription = () => {
    if (isAuthenticated && user?.id) {
      console.log("[Subscription Sync] Refreshing subscription data...");

      // Fetch via Redux action
      dispatch(getActiveSubscription());

      // Invalidate React Query cache to force refetch
      queryClient.invalidateQueries([QUERY_KEYS.activeSubscription]);
      queryClient.invalidateQueries("userEligibility");
    }
  };

  // Initial sync on mount (handles page refresh)
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      console.log("[Subscription Sync] Initial subscription sync on mount");
      refreshSubscription();
    }
  }, [isAuthenticated, user?.id]);

  // Sync on window focus (handles user switching back to tab)
  useEffect(() => {
    const handleFocus = () => {
      if (isAuthenticated && user?.id) {
        console.log(
          "[Subscription Sync] Window focused - syncing subscription"
        );
        refreshSubscription();
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [isAuthenticated, user?.id]);

  // Optional: Sync on visibility change (more comprehensive than focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        isAuthenticated &&
        user?.id
      ) {
        console.log(
          "[Subscription Sync] Tab became visible - syncing subscription"
        );
        refreshSubscription();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, user?.id]);

  return { refreshSubscription };
};
